import './table.scss';
import m, {ClassComponent, Vnode, VnodeDOM} from 'mithril';
import {formatDateTime, formatDelta, formatValue} from '../render/format';
import type {MarkHandle, RenderContext} from '../types';
import {ColumnKind, SortDirection, TableCell, TableColumn, TableColumnGroup, TableRow, TableSort, TableSpec, cellMeasure, columnGroups} from './model';
import {sortRows} from './sortRows';

export interface TableViewAttrs {
    table: TableSpec;
    ctx: RenderContext;
    /** Content of a column group's header cell; the label by default */
    groupHeader?: (group: TableColumnGroup) => m.Children;
    /** Content of a cell instead of its text (the cell itself stays); undefined keeps the text */
    cell?: (column: TableColumn, row: TableRow) => m.Children | undefined;
    /**
     * A trailing column of row actions: not part of `columns`, so neither sorted nor exported, and left out of print and
     * the PNG
     */
    rowEnd?: {header: string; render: (row: TableRow) => m.Children};
}

/** What a mark adds to the view besides the table */
export type TableViewExtras = Pick<TableViewAttrs, 'groupHeader' | 'cell' | 'rowEnd'>;

const ASCENDING_FIRST: ColumnKind[] = ['label', 'rank'];
/** Numbers align to the end, what reads as text to the start */
const START_ALIGNED: ColumnKind[] = ['label', 'text', 'date', 'plain'];

function firstDirection(column: TableColumn): SortDirection {
    return ASCENDING_FIRST.includes(column.kind) ? 'asc' : 'desc';
}

function sameSort(a: TableSort | null | undefined, b: TableSort | null | undefined): boolean {
    return a?.column === b?.column && a?.dir === b?.dir;
}

function cellText(column: TableColumn, cell: TableCell | undefined, ctx: RenderContext): string {
    const value = cell?.value ?? null;
    const measure = cellMeasure(column, cell);
    switch (column.kind) {
        case 'rank':
            return value === null ? '' : `${value}.`;
        case 'count':
            return value === null ? '' : `${value}${cell?.lowBase ? '*' : ''}`;
        case 'delta':
            return typeof value === 'number' ? formatDelta(value, measure, ctx) : '';
        case 'value':
        case 'bar':
            return measure && typeof value !== 'string' ? formatValue(value, measure, ctx) : String(value ?? '');
        case 'date':
            return typeof value === 'string' ? formatDateTime(value, ctx.locale) : '';
        default:
            return String(value ?? '');
    }
}

/** What a delta is the difference to, as the header's tooltip */
function againstTitle(column: TableColumn, ctx: RenderContext): string | undefined {
    const labels = ctx.labels ?? {};
    switch (column.against) {
        case 'previous':
            return labels.againstPrevious ?? 'againstPrevious';
        case 'reference':
            return labels.comparisonVersusReference ?? 'comparisonVersusReference';
        default:
            return undefined;
    }
}

function deltaClass(column: TableColumn, cell: TableCell | undefined): string {
    const value = cell?.value;
    if (column.kind !== 'delta' || !column.good || typeof value !== 'number' || value === 0) {
        return '';
    }

    return Math.sign(value) === column.good ? 'is-better' : 'is-worse';
}

/** Explicit range, else from zero (or the lowest negative) up to the highest value among the rows */
function barRange(column: TableColumn, rows: TableRow[]): [number, number] {
    if (column.range) {
        return column.range;
    }
    const values = rows.map(row => row.cells[column.id]?.value).filter((value): value is number => typeof value === 'number');

    return [Math.min(0, ...values), Math.max(0, ...values)];
}

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

/** Offset and width in % of the track: the bar grows from zero (or the range end nearest to it) towards the value */
export function barGeometry(value: number, [min, max]: [number, number]): {left: number; width: number} {
    if (max <= min) {
        return {left: 0, width: 0};
    }
    const percent = (point: number) => ((clamp(point, min, max) - min) / (max - min)) * 100;
    const zero = percent(0);
    const end = percent(value);

    return {left: Math.min(zero, end), width: Math.abs(end - zero)};
}

/** Key of the trailing action cell; a column id cannot clash, those are cube-derived words */
const ROW_END = '$rowEnd';

const stickyLeft = (position: number): string => `--db-v2-sticky-${position}`;

/**
 * Offsets of the sticky columns as custom properties on the table: each one sticks right after the previous ones.
 * Widths, not offsetLeft: a stuck cell reports its scrolled position.
 */
function measureSticky(table: HTMLTableElement): void {
    let left = 0;
    table.querySelectorAll<HTMLElement>('thead th.is-sticky').forEach((th, position) => {
        table.style.setProperty(stickyLeft(position), `${left}px`);
        left += th.getBoundingClientRect().width;
    });
}

const HEAD_TOP = '--db-v2-thead-top';
const HEAD_MAX = '--db-v2-thead-max';
const STICKY_TOP = '--db-v2-table-sticky-top';

/** The wrapper itself or the nearest ancestor that scrolls vertically; null = the page */
function verticalScroller(wrapper: HTMLElement): HTMLElement | null {
    for (let el: HTMLElement | null = wrapper; el && el !== document.body; el = el.parentElement) {
        const {overflowY} = getComputedStyle(el);
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
            return el;
        }
    }

    return null;
}

/**
 * The header follows the scroll by a transform the CSS clamps to the table: the horizontally scrolling wrapper is a
 * scroll container, so `position: sticky` could only stick to the wrapper, never to the page. On the page the header
 * stops under the app's fixed bars (the CSS offset), in a scrolling element at its top edge.
 */
function stickHead(table: HTMLTableElement, scroller: HTMLElement | null): void {
    const head = table.tHead;
    if (!head) {
        return;
    }
    const edge = scroller ? scroller.getBoundingClientRect().top : 0;
    table.style.setProperty(HEAD_TOP, `${table.getBoundingClientRect().top - edge}px`);
    table.style.setProperty(HEAD_MAX, `${table.offsetHeight - head.offsetHeight}px`);
    if (scroller) {
        table.style.setProperty(STICKY_TOP, '0px');
    } else {
        table.style.removeProperty(STICKY_TOP);
    }
}

/** Table cells a column takes: a bar column has its own cell for the bar */
const span = (column: TableColumn): number => column.kind === 'bar' ? 2 : 1;

const PRINT_ZOOM = '--db-v2-table-print-zoom';
/** Below this the text gets too small to read; the rest of the table is clipped then */
const PRINT_ZOOM_MIN = 0.5;

/**
 * Print cannot scroll, so a table wider than its wrapper is zoomed out to fit it. Measured only while print media
 * applies, as the print layout gives the card another width: Chrome lays the page out for print right after the
 * media change handlers; a page already in print media is fitted on mount and resize.
 */
function fitForPrint(wrapper: HTMLElement, table: HTMLTableElement): void {
    wrapper.style.removeProperty(PRINT_ZOOM);
    if (!window.matchMedia?.('print').matches) {
        return;
    }
    const width = table.offsetWidth;
    if (width > wrapper.clientWidth) {
        // Rounded down, a zoom rounded up could leave a subpixel overflow
        const zoom = Math.floor(wrapper.clientWidth / width * 1000) / 1000;
        wrapper.style.setProperty(PRINT_ZOOM, String(Math.max(PRINT_ZOOM_MIN, zoom)));
    }
}

/** Sortable table of a `TableSpec`; sorting is local to the view, the spec keeps its built order. */
export default class TableView implements ClassComponent<TableViewAttrs> {
    /** null = the order the rows were built in */
    private sort: TableSort | null = null;
    private resizeObserver: ResizeObserver | null = null;
    private printListener: (() => void) | null = null;
    private scrollListener: ((event: Event) => void) | null = null;
    private scrollFrame: number | null = null;
    /** Looked up on layout changes and when something other than it scrolls, not on every scroll */
    private scroller: HTMLElement | null = null;
    private scrollerStale = false;
    /** Column id => position among the sticky columns */
    private sticky = new Map<string, number>();

    oncreate({dom}: VnodeDOM<TableViewAttrs>): void {
        const wrapper = dom as HTMLElement;
        const table = wrapper.querySelector('table');
        if (!table) {
            return;
        }
        this.layout(wrapper, table);
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.layout(wrapper, table));
            // The wrapper too: a table wider than it keeps its size when only the card resizes
            this.resizeObserver.observe(table);
            this.resizeObserver.observe(wrapper);
        }
        this.printListener = () => fitForPrint(wrapper, table);
        window.matchMedia?.('print').addEventListener('change', this.printListener);
        // Capture: scroll does not bubble, and the scrolling element may be any ancestor
        this.scrollListener = (event: Event) => {
            const target = event.target === document ? null : event.target;
            // Another component's scroller, or the wrapper's own horizontal scroll: the header stays where it is
            if ((target instanceof Element && !target.contains(wrapper)) || (target === wrapper && wrapper !== this.scroller)) {
                return;
            }
            // Overflow can change without a resize of the table or wrapper; whatever scrolls now overflows
            if (target !== this.scroller) {
                this.scrollerStale = true;
            }
            // One measure per frame however many scroll events arrive in it
            this.scrollFrame ??= requestAnimationFrame(() => {
                this.scrollFrame = null;
                if (this.scrollerStale) {
                    this.scrollerStale = false;
                    this.scroller = verticalScroller(wrapper);
                }
                stickHead(table, this.scroller);
            });
        };
        document.addEventListener('scroll', this.scrollListener, {capture: true, passive: true});
    }

    onupdate({dom}: VnodeDOM<TableViewAttrs>): void {
        const table = dom.querySelector('table');
        if (table) {
            this.layout(dom as HTMLElement, table);
        }
    }

    onremove(): void {
        this.resizeObserver?.disconnect();
        if (this.printListener) {
            window.matchMedia?.('print').removeEventListener('change', this.printListener);
        }
        if (this.scrollListener) {
            document.removeEventListener('scroll', this.scrollListener, {capture: true});
        }
        if (this.scrollFrame !== null) {
            cancelAnimationFrame(this.scrollFrame);
        }
    }

    private layout(wrapper: HTMLElement, table: HTMLTableElement): void {
        measureSticky(table);
        fitForPrint(wrapper, table);
        this.scroller = verticalScroller(wrapper);
        stickHead(table, this.scroller);
    }

    view({attrs: {table, ctx, groupHeader, cell, rowEnd}}: Vnode<TableViewAttrs>): m.Children {
        if (this.sort && (table.sortable === false || !table.columns.some(column => column.id === this.sort!.column))) {
            this.sort = null;
        }
        const shown = this.sort ?? table.sort ?? null;
        const rows = this.sort ? sortRows(table.rows, this.sort.column, this.sort.dir, ctx.locale) : table.rows;
        this.sticky = new Map(table.columns.filter(column => column.sticky).map((column, position) => [column.id, position]));
        const ranges = Object.fromEntries(table.columns
            .filter(column => column.kind === 'bar')
            .map(column => [column.id, barRange(column, rows)]));
        const groups = columnGroups(table);
        const groupStarts = new Set([
            ...(table.groups ?? []).map(group => group.columns[0]),
            ...table.columns.filter(column => column.divided).map(column => column.id),
        ]);
        const width = table.columns.reduce((sum, column) => sum + span(column), 0) + (rowEnd ? 1 : 0);

        return m('.db-v2-table', m('table.table.table-sm.table-hover.mb-0', [
            m('thead', groups.size === 0
                ? m('tr', [...table.columns.map(column => this.header(table, column, shown, ctx)), ...this.rowEndHeader(rowEnd)])
                : [
                    // Mithril takes no holes among keyed cells, so the columns a group cell covers are left out
                    m('tr', [
                        ...table.columns.flatMap(column => {
                            const group = groups.get(column.id);
                            if (!group) {
                                return [this.header(table, column, shown, ctx, {rowspan: 2, groupStart: groupStarts.has(column.id)})];
                            }

                            return groupStarts.has(column.id) ? [this.groupHeader(table, group, groupHeader)] : [];
                        }),
                        ...this.rowEndHeader(rowEnd, 2),
                    ]),
                    m('tr', table.columns
                        .filter(column => groups.has(column.id))
                        .map(column => this.header(table, column, shown, ctx, {groupStart: groupStarts.has(column.id)}))),
                ]),
            m('tbody', rows.map(row => {
                switch (row.role) {
                    case 'gap':
                        return m('tr.db-v2-table-gap', {key: row.key}, m('td', {colspan: width}, '…'));
                    case 'group':
                        return m('tr.db-v2-table-group', {key: row.key}, m('th', {colspan: width, scope: 'rowgroup'}, m('span', row.label ?? '')));
                    default:
                        return m('tr', {
                            key: row.key,
                            class: [
                                row.role === 'total' ? 'is-total' : '',
                                row.highlight ? `is-${row.highlight}` : '',
                                row.unranked ? 'is-unranked' : '',
                            ].filter(Boolean).join(' '),
                        }, [
                            ...table.columns.map(column => this.cell(column, row.cells[column.id], ctx, ranges[column.id], groupStarts.has(column.id), cell?.(column, row))),
                            ...(rowEnd ? [m('td.d-print-none', {key: ROW_END, 'data-html2canvas-ignore': ''}, rowEnd.render(row))] : []),
                        ]);
                }
            })),
        ]));
    }

    /** An array, as Mithril takes no holes among keyed cells */
    private rowEndHeader(rowEnd: TableViewAttrs['rowEnd'], rowspan?: number): m.Vnode[] {
        return rowEnd ? [m('th.d-print-none', {key: ROW_END, scope: 'col', rowspan, 'data-html2canvas-ignore': ''}, m('span.visually-hidden', rowEnd.header))] : [];
    }

    private groupHeader(table: TableSpec, group: TableColumnGroup, content?: TableViewAttrs['groupHeader']): m.Children {
        const columns = table.columns.filter(column => group.columns.includes(column.id));

        return m('th.db-v2-table-colgroup.is-group-start', {
            key: `group:${group.id}`,
            scope: 'colgroup',
            colspan: columns.reduce((sum, column) => sum + span(column), 0),
        }, content ? content(group) : group.label);
    }

    private header(
        table: TableSpec,
        column: TableColumn,
        shown: TableSort | null,
        ctx: RenderContext,
        {rowspan, groupStart}: {rowspan?: number; groupStart?: boolean} = {},
    ): m.Children {
        const dir = shown?.column === column.id ? shown.dir : null;
        const sticky = this.stickyAttrs(column, [START_ALIGNED.includes(column.kind) ? '' : 'text-end', groupStart ? 'is-group-start' : '', column.tinted ? 'is-tinted' : ''].filter(Boolean).join(' '));

        return m('th', {
            key: column.id,
            scope: 'col',
            colspan: span(column) > 1 ? span(column) : undefined,
            rowspan,
            title: column.title ?? againstTitle(column, ctx),
            ...sticky,
            'aria-sort': dir ? (dir === 'asc' ? 'ascending' : 'descending') : undefined,
        }, table.sortable === false ? column.label : m('button.db-v2-table-sort', {type: 'button', onclick: () => this.toggleSort(table, column)}, [
            column.label,
            m('i.d-print-none', {
                class: dir ? (dir === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'bi-chevron-expand',
                'aria-hidden': 'true',
                'data-html2canvas-ignore': '',
            }),
        ]));
    }

    /** First direction, the opposite one, then back to the built order */
    private toggleSort(table: TableSpec, column: TableColumn): void {
        const current = this.sort ?? table.sort;
        const first = firstDirection(column);
        let next: TableSort | null;
        if (current?.column !== column.id) {
            next = {column: column.id, dir: first};
        } else if (current.dir === first) {
            next = {column: column.id, dir: first === 'asc' ? 'desc' : 'asc'};
        } else {
            next = table.sort?.column === column.id ? {column: column.id, dir: first} : null;
        }
        this.sort = sameSort(next, table.sort) ? null : next;
    }

    private stickyAttrs(column: TableColumn, className: string): {class: string; style?: {left: string}} {
        const position = this.sticky.get(column.id);

        return position === undefined
            ? {class: className}
            : {class: [className, 'is-sticky'].filter(Boolean).join(' '), style: {left: `var(${stickyLeft(position)}, 0px)`}};
    }

    private cell(column: TableColumn, cell: TableCell | undefined, ctx: RenderContext, range?: [number, number], groupStart = false, content?: m.Children): m.Children {
        const text = content ?? cellText(column, cell, ctx);
        const attrs = {
            key: column.id,
            title: cell?.title,
            ...this.stickyAttrs(column, [
                column.kind === 'text' ? 'db-v2-table-text' : 'text-nowrap',
                START_ALIGNED.includes(column.kind) ? '' : 'text-end',
                cell?.lowBase ? 'is-low-base' : '',
                deltaClass(column, cell),
                groupStart && column.kind !== 'bar' ? 'is-group-start' : '',
                column.tinted ? 'is-tinted' : '',
            ].filter(Boolean).join(' ')),
        };

        if (column.kind === 'label') {
            return m('th', {...attrs, scope: 'row'}, [
                cell?.color && m('span.db-v2-table-dot', {style: {color: cell.color}, 'aria-hidden': 'true'}, '●'),
                text,
            ]);
        }
        if (column.kind === 'bar') {
            // Own cell for the bar: the table aligns the tracks across rows regardless of the value text width
            const geometry = range && typeof cell?.value === 'number' ? barGeometry(cell.value, range) : null;

            return m.fragment({key: column.id}, [
                m('td.db-v2-table-bar', {key: 'bar', 'aria-hidden': 'true', class: groupStart ? 'is-group-start' : undefined}, geometry && m('span.db-v2-table-bar-track', m('span.db-v2-table-bar-fill', {
                    style: {marginLeft: `${geometry.left}%`, width: `${geometry.width}%`, background: cell?.color},
                }))),
                m('td', {...attrs, key: 'value'}, text),
            ]);
        }

        return m('td', attrs, text);
    }
}

/** For marks: a mount (not m.render) so the sort clicks redraw it, also on the v1 dashboard that has no Mithril root. */
export function mountTable(el: HTMLElement, table: TableSpec, ctx: RenderContext, extras: TableViewExtras = {}): MarkHandle {
    m.mount(el, {view: () => m(TableView, {table, ctx, ...extras})});

    return {destroy: () => m.mount(el, null)};
}
